import { ROLES } from "./constants/roles";

const getApiDomain = () => {
  const origin = location.origin;
  if (origin.includes("ki.imagineher.org"))
    return "https://ki-api.imagineher.org";
  if (origin.includes(":5173")) return "http://127.0.0.1:3000";
  return "http://127.0.0.1:3000";
};

const domain = getApiDomain();

function normalizeBackendRole(role) {
  const normalized = String(role ?? "")
    .trim()
    .toUpperCase();
  switch (normalized) {
    case "ADMIN":
      return ROLES.ADMIN;
    case "MANAGER":
    case "PROGRAM_MANAGER":
      return ROLES.MANAGER;
    case "FUNDER":
      return ROLES.FUNDER;
    case "VENTURE":
      return ROLES.VENTURE;
    default:
      return null;
  }
}

export function mapRoleToBackend(role) {
  switch (role) {
    case ROLES.ADMIN:
      return "ADMIN";
    case ROLES.MANAGER:
      return "MANAGER";
    case ROLES.FUNDER:
      return "FUNDER";
    case ROLES.VENTURE:
      return "VENTURE";
    default:
      return null;
  }
}

function normalizeAuthHeader(token) {
  if (!token) return "";
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

function getErrorMessage(payload, fallback) {
  if (payload?.error?.detail) return payload.error.detail;
  if (typeof payload?.error === "string") return payload.error;
  if (Array.isArray(payload?.errors) && payload.errors[0]?.message) {
    return payload.errors[0].message;
  }
  return fallback;
}

async function request(path, { method = "GET", body, token } = {}) {
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = normalizeAuthHeader(token);

  const response = await fetch(`${domain}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let payload;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  return { response, payload };
}

//AUTH

export const signin = async ({ email, password }) => {
  const { response, payload } = await request("/signin", {
    method: "POST",
    body: { email, password },
  });

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: getErrorMessage(payload, "Signin failed"),
      payload,
    };
  }

  const token = payload?.message?.token;
  const expiresAt = payload?.message?.expiresAt ?? null;
  const backendUser = payload?.message?.user ?? null;
  const role = normalizeBackendRole(backendUser?.role);

  return {
    ok: true,
    status: response.status,
    payload,
    token,
    expiresAt,
    user: backendUser
      ? {
          ...backendUser,
          role,
        }
      : null,
  };
};

export const forgotPassword = async ({ email }) => {
  const { response, payload } = await request("/forgot-password", {
    method: "POST",
    body: { email },
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    message:
      payload?.message ??
      getErrorMessage(payload, "Unable to request password reset"),
  };
};

export const resetPassword = async ({ token, newPassword }) => {
  const { response, payload } = await request("/reset-password", {
    method: "POST",
    body: { token, newPassword },
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    message:
      payload?.message ?? getErrorMessage(payload, "Unable to reset password"),
  };
};

//user management

export const createUser = async ({
  token,
  name,
  email,
  role,
  phone,
  linkedVenture,
  linkedVentures,
}) => {
  const backendRole = mapRoleToBackend(role);
  const body = {
    name,
    email,
    role: backendRole,
  };

  if (phone) body.phone = phone;
  if (Array.isArray(linkedVentures)) {
    body.linked_ventures = linkedVentures;
  } else if (linkedVenture) {
    body.linked_venture = linkedVenture;
  }

  const { response, payload } = await request("/users", {
    method: "POST",
    body,
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    user: payload?.message?.user ?? null,
    inviteEmailSent: payload?.message?.inviteEmailSent ?? false,
    error: getErrorMessage(payload, "Unable to create user"),
  };
};

export const getUsers = async ({ token }) => {
  const { response, payload } = await request("/users", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    users: payload?.users ?? [],
    error: getErrorMessage(payload, "Unable to fetch users"),
  };
};

export const activateUser = async ({ token, userId }) => {
  const { response, payload } = await request(`/users/${userId}/activate`, {
    method: "POST",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    message: payload?.message ?? "User activated successfully",
    error: getErrorMessage(payload, "Unable to activate user"),
  };
};

export const deactivateUser = async ({ token, userId }) => {
  const { response, payload } = await request(`/users/${userId}/deactivate`, {
    method: "POST",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    message: payload?.message ?? "User deactivated successfully",
    error: getErrorMessage(payload, "Unable to deactivate user"),
  };
};

export const updateUser = async ({
  token,
  userId,
  name,
  email,
  role,
  phone,
  linkedVenture,
  linkedVentures,
}) => {
  const backendRole = mapRoleToBackend(role);
  const body = {
    name,
    email,
    role: backendRole,
    phone: phone || null,
    linked_venture: linkedVenture || null,
  };

  if (Array.isArray(linkedVentures)) {
    body.linked_ventures = linkedVentures;
  }

  const { response, payload } = await request(`/users/${userId}`, {
    method: "POST",
    body,
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    user: payload?.message?.user ?? null,
    error: getErrorMessage(payload, "Unable to update user"),
  };
};

//dashboard

const DASHBOARD_PATHS = {
  [ROLES.ADMIN]: "/admin/dashboard",
  [ROLES.MANAGER]: "/manager/dashboard",
  [ROLES.FUNDER]: "/funder/dashboard",
  [ROLES.VENTURE]: "/venture/dashboard",
};

export const getDashboard = async ({ token, role }) => {
  const path = DASHBOARD_PATHS[role];
  if (!path) {
    return {
      ok: false,
      status: 400,
      payload: null,
      error: "Unsupported role for dashboard",
    };
  }

  const { response, payload } = await request(path, { token });
  return {
    ok: response.ok,
    status: response.status,
    payload,
    data: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to load dashboard"),
  };
};

export const getVentureSupport = async ({ token }) => {
  const { response, payload } = await request("/venture/support", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    support: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch support data"),
  };
};

export const getVentureDueDiligence = async ({ token }) => {
  const { response, payload } = await request("/venture/due-diligence", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    dueDiligence: payload?.data ?? null,
    error: getErrorMessage(
      payload,
      "Unable to fetch venture due diligence score",
    ),
  };
};

//ADMIN ROUTES

export const getAdminDashboard = async ({ token }) => {
  return getDashboard({ token, role: ROLES.ADMIN });
};

export const getAdminVentures = async ({ token }) => {
  const { response, payload } = await request("/admin/ventures", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    ventures: payload?.data ?? [],
    error: getErrorMessage(payload, "Unable to fetch ventures"),
  };
};

export const getManagerVentures = async ({ token }) => {
  const { response, payload } = await request("/manager/ventures", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    ventures: payload?.data ?? [],
    error: getErrorMessage(payload, "Unable to fetch ventures"),
  };
};

export const getAdminVenture = async ({ token, ventureId }) => {
  const encodedVentureId = encodeURIComponent(String(ventureId));
  const { response, payload } = await request(
    `/admin/ventures/${encodedVentureId}`,
    {
      method: "GET",
      token,
    },
  );

  return {
    ok: response.ok,
    status: response.status,
    payload,
    venture: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch venture details"),
  };
};

export const getManagerVenture = async ({ token, ventureId }) => {
  const encodedVentureId = encodeURIComponent(String(ventureId));
  const { response, payload } = await request(
    `/manager/ventures/${encodedVentureId}`,
    {
      method: "GET",
      token,
    },
  );

  return {
    ok: response.ok,
    status: response.status,
    payload,
    venture: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch venture details"),
  };
};

export const getAdminVentureDueDiligence = async ({ token, ventureId }) => {
  const encodedVentureId = encodeURIComponent(String(ventureId));
  const { response, payload } = await request(
    `/admin/ventures/${encodedVentureId}/due-diligence`,
    {
      method: "GET",
      token,
    },
  );

  return {
    ok: response.ok,
    status: response.status,
    payload,
    dueDiligence: payload?.data ?? null,
    error: getErrorMessage(
      payload,
      "Unable to fetch venture due diligence score",
    ),
  };
};

export const getManagerVentureDueDiligence = async ({ token, ventureId }) => {
  const encodedVentureId = encodeURIComponent(String(ventureId));
  const { response, payload } = await request(
    `/manager/ventures/${encodedVentureId}/due-diligence`,
    {
      method: "GET",
      token,
    },
  );

  return {
    ok: response.ok,
    status: response.status,
    payload,
    dueDiligence: payload?.data ?? null,
    error: getErrorMessage(
      payload,
      "Unable to fetch venture due diligence score",
    ),
  };
};

export const getAdminImpact = async ({ token }) => {
  const { response, payload } = await request("/admin/impact", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    impact: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch impact data"),
  };
};

export const getManagerImpact = async ({ token }) => {
  const { response, payload } = await request("/manager/impact", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    impact: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch impact data"),
  };
};

export const getAdminPortfolio = async ({ token }) => {
  const { response, payload } = await request("/admin/portfolio", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    portfolio: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch portfolio data"),
  };
};

export const getManagerPortfolio = async ({ token }) => {
  const { response, payload } = await request("/manager/portfolio", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    portfolio: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch portfolio data"),
  };
};

export const getAdminDataCollection = async ({ token }) => {
  const { response, payload } = await request("/admin/data-collection", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    dataCollection: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch data collection data"),
  };
};

export const getManagerDataCollection = async ({ token }) => {
  const { response, payload } = await request("/manager/data-collection", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    dataCollection: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch data collection data"),
  };
};

export const getFunderImpact = async ({ token }) => {
  const { response, payload } = await request("/funder/impact", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    impact: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch impact data"),
  };
};

export const getFunderFinancial = async ({ token }) => {
  const { response, payload } = await request("/funder/financial", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    financial: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch financial data"),
  };
};

export const getFunderPrograms = async ({ token }) => {
  const { response, payload } = await request("/funder/programs", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    programs: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch programs data"),
  };
};

export const getFunderReports = async ({ token }) => {
  const { response, payload } = await request("/funder/reports", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    reports: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch reports data"),
  };
};

export const getAdminReports = async ({ token }) => {
  const { response, payload } = await request("/admin/reports", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    reports: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch reports data"),
  };
};

export const getManagerReports = async ({ token }) => {
  const { response, payload } = await request("/manager/reports", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    reports: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch reports data"),
  };
};

export const getAdminSettings = async ({ token }) => {
  const { response, payload } = await request("/admin/settings", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    settings: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch settings data"),
  };
};

export const getAdminTraining = async ({ token }) => {
  const { response, payload } = await request("/admin/training", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    training: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch training data"),
  };
};

export const getManagerTraining = async ({ token }) => {
  const { response, payload } = await request("/manager/training", {
    method: "GET",
    token,
  });

  return {
    ok: response.ok,
    status: response.status,
    payload,
    training: payload?.data ?? null,
    error: getErrorMessage(payload, "Unable to fetch training data"),
  };
};
